import React, {useState, useEffect} from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    IconButton,
    Modal,
    Fade,
    Backdrop,
    CircularProgress
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ImageIcon from '@material-ui/icons/Image';
import CloseIcon from '@material-ui/icons/Close';

const EquipmentSelection = ({equipmentData, selectedEquipment, onEquipmentChange, loading = false, slotCount = 1}) => {
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [imageModal, setImageModal] = useState({open: false, src: ''});

    // Инициализация выбранного оборудования
    useEffect(() => {
        if (equipmentData && equipmentData.length > 0 && !selectedEquipment) {
            // Инициализируем пустой объект для выбранного оборудования
            const initialSelected = {};
            equipmentData.forEach(category => {
                category.equipment.forEach(item => {
                    initialSelected[item.id] = 0;
                });
            });
            onEquipmentChange(initialSelected);
        }
    }, [equipmentData, selectedEquipment, onEquipmentChange]);

    const handleCategoryToggle = (categoryId) => {
        setExpandedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const handleQuantityChange = (equipmentId, delta) => {
        if (!selectedEquipment) return;

        const currentQuantity = selectedEquipment[equipmentId] || 0;
        const equipmentItem = findEquipmentById(equipmentId);

        if (!equipmentItem) return;

        const newQuantity = currentQuantity + delta;

        // Проверяем доступное количество
        if (newQuantity < 0) return;
        if (newQuantity > equipmentItem.available_count) return;

        const updatedEquipment = {...selectedEquipment, [equipmentId]: newQuantity};
        onEquipmentChange(updatedEquipment);
    };

    const findEquipmentById = (id) => {
        if (!equipmentData) return null;

        for (const category of equipmentData) {
            const equipment = category.equipment.find(item => item.id === id);
            if (equipment) return equipment;
        }
        return null;
    };

    const handleImageClick = (imageUrl) => {
        if (imageUrl) {
            setImageModal({open: true, src: imageUrl});
        }
    };

    const renderEquipment = (equipment) => {
        const isDisabled = equipment.available_count === 0;
        const currentQuantity = selectedEquipment ? selectedEquipment[equipment.id] || 0 : 0;

        return (
            <div key={equipment.id} className={`equipment-item ${isDisabled ? 'disabled' : ''}`}>
                <div className="equipment-image-block" onClick={() => handleImageClick(equipment.image_url)}>
                    {equipment.image_url ? (
                        <img
                            src={(process.env.NODE_ENV === 'development' ? 'http://tonesky.darlane/' : '') + equipment.image_url}
                            alt={equipment.name}
                            className="equipment-image"
                        />
                    ) : (
                        <div className="equipment-image-placeholder">
                            <ImageIcon className="placeholder-icon"/>
                        </div>
                    )}
                </div>

                <div className="equipment-info">
                    <div className="equipment-title">{equipment.name}</div>
                    <div className="equipment-details">
                        <div className="equipment-count">
                            Всего: <span className="equipment-count-value">{equipment.total_count}</span>
                        </div>
                        <div className="equipment-price">
                            Цена: <span
                            className="equipment-price-value">{slotCount > 1 ? (parseFloat(equipment.price) * slotCount) : equipment.price}</span>р.
                        </div>
                    </div>

                </div>

                <div className="equipment-counter">
                    <div>
                        <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(equipment.id, -1)}
                            disabled={currentQuantity <= 0 || isDisabled}
                            className="counter-button"
                        >
                            <RemoveIcon/>
                        </IconButton>

                        <span className="equipment-quantity">{currentQuantity}</span>

                        <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(equipment.id, 1)}
                            disabled={currentQuantity >= equipment.available_count || isDisabled}
                            className="counter-button"
                        >
                            <AddIcon/>
                        </IconButton>
                    </div>
                    <div className="equipment-available">
                        Доступно: <span className="equipment-available-value">{equipment.available_count}</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderCategory = (category) => (
        <Accordion
            key={category.id}
            expanded={expandedCategories.includes(category.id)}
            onChange={() => handleCategoryToggle(category.id)}
            className="equipment-category"
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography className="category-title">{category.name}</Typography>
            </AccordionSummary>
            <AccordionDetails className="equipment-category-details">
                <div className="equipment-list">
                    {category.equipment.map(renderEquipment)}
                </div>
            </AccordionDetails>
        </Accordion>
    );

    if (loading) {
        return (
            <div className="equipment-loading">
                <CircularProgress color="secondary"/>
                <Typography className="loading-text">Загрузка оборудования...</Typography>
            </div>
        );
    }

    if (!equipmentData || equipmentData.length === 0) {
        return (
            <div className="equipment-empty">
                <Typography className="empty-text">Оборудование не доступно для выбранного времени</Typography>
            </div>
        );
    }

    return (
        <div className="equipment-selection">
            <div className="equipment-header">
                <Typography variant="h6" className="equipment-title">
                    Дополнительное оборудование
                </Typography>
            </div>

            <div className="equipment-categories">
                {equipmentData.map(renderCategory)}
            </div>

            {/* Модальное окно для просмотра изображения */}
            <Modal
                open={imageModal.open}
                onClose={() => setImageModal({open: false, src: ''})}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{timeout: 500}}
                className="equipment-image-modal"
            >
                <Fade in={imageModal.open}>
                    <div className="equipment-image-modal-content">
                        <div className="equipment-modal-inner">
                            <IconButton
                                className="equipment-modal-close-button"
                                onClick={() => setImageModal({open: false, src: ''})}
                                size="small"
                                aria-label="Закрыть"
                            >
                                <CloseIcon/>
                            </IconButton>
                            <img
                                src={(process.env.NODE_ENV === 'development' ? 'http://tonesky.darlane/' : '') + imageModal.src}
                                alt="Оборудование"
                                className="equipment-modal-image"
                            />
                        </div>
                    </div>
                </Fade>
            </Modal>
        </div>
    );
};

export default EquipmentSelection;